import {Component, ElementRef, OnInit, TemplateRef, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {VisitContext} from '../visit-context';
import moment from 'moment';

import {
  Berth,
  BerthVisit,
  BerthVisitInfo,
  BoatmenService,
  CancelVisit,
  Declaration,
  DeclarationType,
  DeclareTerminalPlanning,
  DeclareVisit,
  FinishTerminalPlanning,
  FormPetroleumPort,
  FormVessels,
  HelmsmenService,
  HinterlandEntry,
  IncomingOrder,
  LocalMovement,
  OrderCancellationReason,
  OrderDeadlines,
  OrderStatus,
  PilotService,
  PortVisit,
  SaveNextVisitDeclaration,
  SaveVisit,
  SeaEntry,
  TugboatService,
  Visit,
  VisitDeclaration
} from '@portbase/bezoekschip-service-typescriptmodels';
import {
  checkValidity,
  clearValidation,
  openConfirmationModalWithCallback,
  sendCommand,
  sendQuery,
  toTitleCase,
  uuid
} from '../../common/utils';
import {AppContext} from '../../app-context';
import {Alert} from "../../common/status-alert/alert";
import {ModalConfirmAutofocus, ModalConfirmAutofocusData} from 'src/app/common/modal/modal-confirm.component';
import {Router} from "@angular/router";
import lodash, {cloneDeep} from "lodash";
import {forkJoin, Observable, of} from "rxjs";
import {LoadDischargeInfo} from "./berth-visit/berth-visit-details/multi-berth/multi-berth.component";
import {DurationComponent} from "../../common/duration/duration.component";
import {EmergencyProcedureService} from "../../admin/emergencyprocedure/emergencyprocedure.service";
import {
  attachmentRequired,
  inertTanksRequired,
  vesselsRequired
} from "./exemptions/common/exemption_utils/exemption.utils";
import * as Sentry from '@sentry/angular';

@Component({
    selector: 'app-visit',
    templateUrl: './visit.component.html',
    styleUrls: ['./visit.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class VisitComponent implements OnInit {
  context = VisitContext;
  appContext = AppContext;
  sendWarnings: string[] = [];
  cancellingOrder: boolean = false;
  berthVisitsInChronologicalOrder: BerthVisit[];
  @ViewChild("berthVisitChronologicalOrder") berthVisitChronologicalRef: TemplateRef<any>;

  constructor(private router: Router,
              private element: ElementRef,
              private emergencyProcedureService: EmergencyProcedureService) {
  }

  ngOnInit(): void {
    const visit = this.context.visit;

    if (visit.visitDeclaration.portVisit.berthVisits.length === 0 && !visit.orderIncomingMovement) {
      visit.visitDeclaration.portVisit.berthVisits.push(<BerthVisit>{
        id: uuid(),
        nextMovement: {},
        visitPurposes: []
      });
    }
  }

  saveVisit = () => {
    clearValidation(this.element);

    this.processMultiBerths(VisitContext.visit.visitDeclaration).subscribe(s => {
      this.berthVisitsInChronologicalOrder = VisitContext.berthVisitsInChronologicalOrder(VisitContext.visit);
      if (VisitContext.visit.terminalPlanningEnabled && !lodash.isEqual(this.berthVisitsInChronologicalOrder, VisitContext.visit.visitDeclaration.portVisit.berthVisits)) {
        openConfirmationModalWithCallback((confirmed) => {
          if (confirmed === null) {
            return;
          }
          if (confirmed) {
            VisitContext.visit.visitDeclaration.portVisit.berthVisits = this.berthVisitsInChronologicalOrder;
          }
          sendSaveVisitCommand();
        }, ModalConfirmAutofocus, <ModalConfirmAutofocusData>{
          type: "info",
          modalSize: 'xl',
          title: "Berth visits not in chronological order",
          confirmText: "Save suggested version",
          cancelText: "Save my version",
          body: this.berthVisitChronologicalRef
        }, 'static');
      } else {
        sendSaveVisitCommand();
      }
    });

    function sendSaveVisitCommand() {
      sendCommand('com.portbase.bezoekschip.common.api.visit.SaveVisit', <SaveVisit>{
        crn: VisitContext.visit.crn,
        visitDeclaration: VisitContext.visit.visitDeclaration
      }, () => {
        VisitContext.replaceVisit(VisitContext.visit);
        AppContext.registerSuccess('The visit was saved successfully.');
      });
    }
  };

  declareTerminalPlanning = () => {

    if (checkValidity(this.element)) {
      if (this.validForTerminalPlanning(VisitContext.visit)) {
        this.processMultiBerths(VisitContext.visit.visitDeclaration).subscribe(s => {
          this.berthVisitsInChronologicalOrder = VisitContext.berthVisitsInChronologicalOrder(VisitContext.visit);
          if (VisitContext.visit.terminalPlanningEnabled && !lodash.isEqual(this.berthVisitsInChronologicalOrder, VisitContext.visit.visitDeclaration.portVisit.berthVisits)) {
            openConfirmationModalWithCallback((confirmed) => {
              if (confirmed === null) {
                return;
              }
              if (confirmed) {
                VisitContext.visit.visitDeclaration.portVisit.berthVisits = this.berthVisitsInChronologicalOrder;
              }
              sendDeclareTerminalPlanningCommand();
            }, ModalConfirmAutofocus, <ModalConfirmAutofocusData>{
              type: "info",
              modalSize: 'xl',
              title: "Berth visits not in chronological order",
              confirmText: "Save and send suggested version",
              cancelText: "Save and send my version",
              body: this.berthVisitChronologicalRef
            }, 'static');
          } else {
            sendDeclareTerminalPlanningCommand();
          }
        });
      }
    }

    function sendDeclareTerminalPlanningCommand() {
      sendCommand('com.portbase.bezoekschip.common.api.visit.DeclareTerminalPlanning', <DeclareTerminalPlanning>{
        crn: VisitContext.visit.crn,
        vessel: VisitContext.visit.vessel,
        visitDeclaration: VisitContext.visit.visitDeclaration,
      }, () => {
        VisitContext.replaceVisit(VisitContext.visit);
        AppContext.registerSuccess('The terminal planning was declared successfully.');
      });
    }
  };

  validForTerminalPlanning = (visit: Visit) => {
    if (!visit.terminalPlanningEnabled) {
      return true;
    }
    const cancelledVisits = this.getCancelledTerminalPlannings(visit);
    if (cancelledVisits.length > 0) {
      const multipleCancelled = cancelledVisits.length > 1;
      AppContext.registerError(`Terminal planning${multipleCancelled ? 's' : ''} with callId (${cancelledVisits.map(bv => bv.callId).join(", ")})
          ${multipleCancelled ? 'are' : 'is'} cancelled and must be removed in order to save this visit.`);
    }
    return !AppContext.hasErrors();
  }

  getCancelledTerminalPlannings = (visit: Visit): BerthVisit[] =>
    visit.visitDeclaration.portVisit.berthVisits.filter(bv => {
      if (!bv.terminalPlanningEnabled) {
        return false;
      }
      const terminalVisit = VisitContext.getTerminalVisit(bv);
      return terminalVisit && terminalVisit.status?.cancelled;
    });

  trySendVisit = () => {
    this.sendWarnings = [];
    this.cancellingOrder = false;
    if (this.context.visit.orderIncomingMovement) {
      this.setEtaPort();
    }

    function servicesRequiredWithin12Hours(order: Order, now: moment.Moment) {
      return (order.pilotService?.required || order.tugboatAtDeparture?.required || order.boatmenAtDeparture?.required)
        && moment(order.etd).subtract(12, "hours").isBefore(now);
    }

    function getCurrentBerthVisitByid(orderId: string) {
      return VisitContext.visit.visitDeclaration.portVisit.berthVisits.find(value => value.id === orderId);
    }

    if (this.validateVisit() && this.validForTerminalPlanning(VisitContext.visit)) {
      //timing warnings
      const now = moment();
      const etaPort = moment(VisitContext.visit.visitDeclaration.portVisit.etaPort);
      const etdPort = moment(VisitContext.visit.visitDeclaration.portVisit.etdPort);
      const tidalGateDisclaimer = 'All tidal window information is provided \"as is,\" with no assurance or guarantee of correctness, completeness, accuracy or timeliness of the information, and without warranty of any kind, express or implied. Use of this information is at your own risk and the Port of Rotterdam Authority therefore accepts no liability whatsoever for any damage, loss or any other liabilities that result (direct or indirectly) from your and/or any third party’s use of the information provided.'
      const krveTerms = "The <a href=\"https://krve.nl/wp-content/uploads/NBC-2023-ENG.pdf\" target=\"_blank\">terms and conditions<\a> of KRVE apply.";
      let krveOrdered: boolean = false;
      let tidalRestricted: boolean = false;
      if (etaPort.isBefore(now) && VisitContext.getVisitStatus() === 'EXPECTED') {
        this.sendWarnings.push("Estimated time of arrival port is in the past");
      }
      if (etdPort.isAfter(moment().add(1, 'years'))) {
        this.sendWarnings.push("Estimated time of departure port is more than 12 months in the future");
      }
      if (VisitContext.visit.visitDeclaration.portVisit.berthVisits.find(
        v => v.ata && !!VisitContext.savedVisit.visitDeclaration.portVisit.berthVisits.find(sv => sv.id === v.id && !sv.ata))) {
        this.sendWarnings.push("Once a berth ATA is set you are not allowed to change the ATA anymore");
      }

      //incoming order warnings
      if (VisitContext.visit.orderIncomingMovement && VisitContext.isExpected()) {
        const incomingOrder: IncomingOrder = this.context.getIncomingOrder(VisitContext.visit);
        const savedIncomingOrder: IncomingOrder = this.context.getIncomingOrder(VisitContext.savedVisit);
        let portVisit = this.context.visit.visitDeclaration.portVisit;
        let dependency = portVisit.portEntry.origin === "SEA" ? (<SeaEntry>portVisit.portEntry).entryDependency : null;
        let dependentVesselHasNotBeenOrdered = incomingOrder.ordered && dependency
          && !this.isOrderedOrConfirmed(dependency.orderStatus);

        if (dependentVesselHasNotBeenOrdered) {
          if (dependency.dependencyType === "EXCHANGE") {
            this.sendWarnings.push("The movement of " + dependency.vesselName + " has not been ordered yet. Harbour master might reject your order of the incoming movement.");
          }
          if (dependency.dependencyType === "ENTRY_AFTER") {
            this.sendWarnings.push("The movement of " + dependency.vesselName + ", arriving before your vessel, has not been ordered yet.");
          }
        }
        if (this.etaPbpIsBeforeEtdBerth(incomingOrder)) {
          this.sendWarnings.push("The ETA pilot boarding place is before the departure of " + dependency.vesselName
            + ". The harbour master may reject your order.");
        }

        if (this.etaSecondVesselIsWithin1HourOfSecondVessel(incomingOrder)) {
          this.sendWarnings.push("The arrival at first berth is earlier than or within one hour of the arrival of " + dependency.vesselName
            + ". The harbour master may reject your order.");
        }
        if (incomingOrder.ordered && (!savedIncomingOrder || !savedIncomingOrder.ordered) && !(dependency && dependency.autoOrder)) {
          let krveOrderedInbound = !!incomingOrder && incomingOrder.boatmenAtArrival && incomingOrder.boatmenAtArrival.required && incomingOrder.boatmenAtArrival.serviceProvider.name === 'KRVE';
          let incomingTidalWindowStatus = this.context.visit.incomingTidalWindowStatus;
          let tidalRestrictedInbound = !!incomingTidalWindowStatus && incomingTidalWindowStatus.tidalShip;
          krveOrdered = krveOrderedInbound;
          tidalRestricted = tidalRestrictedInbound;

          this.sendWarnings.push("You are about to order the incoming movement.");
          if (!this.context.hasDeclaredOrAcceptedMdoh()) {
            if (moment(now).isBefore(moment(portVisit.portEntry.etaPilotBoardingPlace).subtract(6, "hours"))) {
              this.sendWarnings.push("Please note, you must submit a valid maritime declaration of health at least 6 hours before ETA pilot boarding place. Without this declaration, " +
                "the harbour master cannot approve your order.");
            } else {
              this.sendWarnings.push("Please note, you must submit a valid maritime declaration of health. Without this declaration, " +
                "the harbour master cannot approve your order.");
            }
          }
        }
        if (!incomingOrder.ordered && savedIncomingOrder.ordered) {
          this.cancellingOrder = true;
          this.sendWarnings.push("You are about to cancel the incoming movement");
        }
        if (incomingOrder.ordered && savedIncomingOrder.ordered && JSON.stringify(incomingOrder) !== JSON.stringify(savedIncomingOrder)) {
          this.sendWarnings.push("You have changed information regarding your order of the incoming movement");
        }
        if (portVisit.pilotStation && (portVisit.pilotStation.code === "MC" || portVisit.pilotStation.code === "KP")) {
          if (moment(portVisit.portEntry.etaPilotBoardingPlace).isBefore(moment((<SeaEntry>portVisit.portEntry).etaSeaBuoy))) {
            this.sendWarnings.push("The ETA pilot boarding place is before the ETA sea buoy");
          }
        }
        if (this.showFirstTimeAutoOrderMessage()) {
          if (dependency.dependencyType === 'EXCHANGE') {
            this.sendWarnings.push("You have chosen automatic ordering. As an agent, you remain responsible for submitting an order. " +
              "Your order will be sent when the departing ship with which you want to exchange is ordered. " +
              "Your ETA will be updated automatically when the ETD of the other vessel changes. " +
              "Your order will be automatically canceled if the order of the departing ship is canceled.");
          } else {
            this.sendWarnings.push("You have chosen automatic ordering. As an agent, you remain responsible for submitting an order. " +
              "Your order will be sent when the ship which you want to enter after is ordered. " +
              "Your ETA will be updated automatically when the ETA of the other vessel changes. " +
              "Your order will be automatically canceled if the order of the departing ship is canceled.");
          }
        }
      }

      if (VisitContext.visit.orderNauticalServices) {
        //order warnings
        const orders: Order[] = getOrders(VisitContext.visit);
        const savedOrders: Order[] = getOrders(VisitContext.savedVisit);
        let newOrder = orders.find(o => !savedOrders.find(so => so.id === o.id));
        if ((orders.length > 0 && !VisitContext.findLatestDeclaration(DeclarationType.VISIT)) || newOrder) {
          let krveOrderedShifting = !!newOrder && newOrder.boatmenAtDeparture.required && newOrder.boatmenAtDeparture.serviceProvider.name === 'KRVE';
          let tidalRestrictedShifting = !!newOrder && newOrder.id && !!this.context.visit.berthVisitInfos[newOrder.id]
            && this.context.visit.berthVisitInfos[newOrder.id].harbourMasterInfo
            && this.context.visit.berthVisitInfos[newOrder.id].harbourMasterInfo.tidalWindowStatus
            && this.context.visit.berthVisitInfos[newOrder.id].harbourMasterInfo.tidalWindowStatus.tidalShip;
          this.sendWarnings.push("You are about to order movement(s).");
          krveOrdered = krveOrdered || krveOrderedShifting;
          tidalRestricted = tidalRestricted || tidalRestrictedShifting;
        }
        if (savedOrders.find(so => !orders.find(o => so.id === o.id))) {
          this.cancellingOrder = true;
          this.sendWarnings.push("You are about to cancel previously ordered movement(s)");
        }
        if (orders.find(o => {
          const savedOrder = savedOrders.find(so => so.id === o.id);
          return savedOrder && JSON.stringify(savedOrder) !== JSON.stringify(o);
        })) {
          this.sendWarnings.push("You have changed information regarding your order of pilots/tugboats/boatmen");
        }

        orders.filter(value => value.atd == null)
          .filter(order => servicesRequiredWithin12Hours(order, now))
          .filter(order => this.context.visit.vessel.lngShip
            || getCurrentBerthVisitByid(order.id)?.nextMovement.vesselDraft > 17.4)
          .map(order => this.context.berthVisitById(order.id))
          .filter(berthVisit => berthVisit != null)
          .map(berthVisit => toTitleCase(berthVisit.berth.name))
          .forEach(berth =>
            this.sendWarnings.push("This ship needs to be ordered at least 12 hours before departure from " + berth));
      }

      //first movement warnings
      if (VisitContext.visit.visitDeclaration.portVisit.firstMovement) {
        if (VisitContext.visit.visitDeclaration.portVisit.firstMovement.numberOfCrew === 0) {
          this.sendWarnings.push("Number of crew on first movement is 0");
        }
      }

      //BerthVisit removed while there are containers being discharged
      const removedBerthVisitsWithCargo = this.getRemovedBerthVisitsWithCargo();
      if (removedBerthVisitsWithCargo.length > 0) {
        const removedBerthVisitsWarning = removedBerthVisitsWithCargo.map(bv => `<li>${bv.berth.terminalName}</li>`).join("\n");
        this.sendWarnings.push("The following removed berth visits have cargo declared, please move the cargo to another terminal." +
          `<ul>${removedBerthVisitsWarning}</ul>`);
      }

      if (krveOrdered) {
        this.sendWarnings.push(krveTerms);
      }
      if (tidalRestricted) {
        this.sendWarnings.push(tidalGateDisclaimer);
      }

      if (this.sendWarnings.length === 0) {
        this.doSendVisit();
      } else {
        this.showSendWarningsModal(this.cancellingOrder);
      }
    }
  };

  private isOrderedOrConfirmed(orderStatus: OrderStatus) {
    return ["ORDERED", "CONFIRMED", "ETD_CHANGED", "ETA_CHANGED"].indexOf(orderStatus) > -1;
  }

  private getRemovedBerthVisitsWithCargo = () => {
    const savedBerthVisits = VisitContext.savedVisit.visitDeclaration.portVisit.berthVisits;
    const berthVisits = VisitContext.visit.visitDeclaration.portVisit.berthVisits;
    return lodash.differenceWith(savedBerthVisits, berthVisits, (a, b) => a.id === b.id)
      .filter(bv => bv.berth?.terminalCode)
      .filter(bv => this.getConsignmentsByTerminal(VisitContext.visit, bv.berth.terminalCode)) || [];
  }

  private getConsignmentsByTerminal = (visit: Visit, terminalCode: string) =>
    lodash.flatMap(visit.importDeclarations || [], (i => i.consignments
      ?.filter(c => c.terminal?.terminalCode === terminalCode)));

  private showFirstTimeAutoOrderMessage() {
    let portEntry = this.context.visit.visitDeclaration.portVisit.portEntry;
    return portEntry.origin === 'SEA'
      && (<SeaEntry>portEntry).entryDependency && (<SeaEntry>portEntry).entryDependency.autoOrder
      && (((<SeaEntry>this.context.savedVisit.visitDeclaration.portVisit.portEntry).entryDependency
        && !(<SeaEntry>this.context.savedVisit.visitDeclaration.portVisit.portEntry).entryDependency.autoOrder) || !(<SeaEntry>this.context.savedVisit.visitDeclaration.portVisit.portEntry).entryDependency)
      && !this.context.visit.visitDeclaration.portVisit.firstMovement.order;
  }

  private etaPbpIsBeforeEtdBerth(incomingOrder: IncomingOrder) {
    let portVisit = this.context.visit.visitDeclaration.portVisit;
    let dependency = portVisit.portEntry.origin === "SEA" ? (<SeaEntry>portVisit.portEntry).entryDependency : null;
    let isExchange = incomingOrder.ordered && dependency && dependency.dependencyType === "EXCHANGE";

    return isExchange && this.isBerthInEuropoortORVesselLargerThan200Meters() && !dependency.autoOrder
      && dependency.estimatedTimeBerth && this.context.visit.visitDeclaration.portVisit.portEntry.etaPilotBoardingPlace
      && moment(this.context.visit.visitDeclaration.portVisit.portEntry.etaPilotBoardingPlace)
        .isBefore(moment(dependency.estimatedTimeBerth));
  }

  private etaSecondVesselIsWithin1HourOfSecondVessel(incomingOrder: IncomingOrder) {
    let portVisit = this.context.visit.visitDeclaration.portVisit;
    let dependency = portVisit.portEntry.origin === "SEA" ? (<SeaEntry>portVisit.portEntry).entryDependency : null;
    let isEntryAfter = incomingOrder.ordered && dependency && dependency.dependencyType === "ENTRY_AFTER";

    return isEntryAfter && !dependency.autoOrder && dependency.estimatedTimeBerth &&
      this.context.visit.visitDeclaration.portVisit.berthVisits[0].eta && moment(dependency.estimatedTimeBerth)
        .add(1, "hour").isAfter(moment(this.context.visit.visitDeclaration.portVisit.berthVisits[0].eta));
  }

  private isBerthInEuropoortORVesselLargerThan200Meters() {
    let hasFirstBerth = this.context.visit.visitDeclaration.portVisit.berthVisits[0] && this.context.visit.visitDeclaration.portVisit.berthVisits[0].berth != null;

    let isBerthInEuropoort = false;
    if (this.context.visit.vessel.fullLength >= 200) {
      return true;
    }
    if (hasFirstBerth) {
      sendQuery('com.portbase.bezoekschip.common.api.visit.IsBerthInEuroPoort', {
        berthCode:
        this.context.visit.visitDeclaration.portVisit.berthVisits[0].berth.code
      })
        .subscribe(result => {
          isBerthInEuropoort = result;
        });
    }
    return isBerthInEuropoort;
  }

  doSendVisit = () => {
    this.sendWarnings = [];
    if (this.context.visit.orderIncomingMovement) {
      this.setEtaPort();
    }

    if (this.validateVisit()) {
      this.syncTimesForOrders();
      this.setPositionsForBerthsWithBollards();

      this.processMultiBerths(VisitContext.visit.visitDeclaration)
        .subscribe(s => {
          this.berthVisitsInChronologicalOrder = VisitContext.berthVisitsInChronologicalOrder(VisitContext.visit);
          if (VisitContext.visit.terminalPlanningEnabled && !lodash.isEqual(this.berthVisitsInChronologicalOrder, VisitContext.visit.visitDeclaration.portVisit.berthVisits)) {
            openConfirmationModalWithCallback((confirmed) => {
              if (confirmed === null) {
                return;
              }
              if (confirmed) {
                VisitContext.visit.visitDeclaration.portVisit.berthVisits = this.berthVisitsInChronologicalOrder;
              }
              sendDeclareVisitCommand();
            }, ModalConfirmAutofocus, <ModalConfirmAutofocusData>{
              type: "info",
              modalSize: 'xl',
              title: "Berth visits not in chronological order",
              confirmText: "Save and send suggested version",
              cancelText: "Save and send my version",
              body: this.berthVisitChronologicalRef
            }, 'static');
          } else {
            sendDeclareVisitCommand();
          }
        });
    }

    function sendDeclareVisitCommand() {
      sendCommand('com.portbase.bezoekschip.common.api.visit.DeclareVisit', <DeclareVisit> {
        crn: VisitContext.visit.crn,
        visitDeclaration: VisitContext.visit.visitDeclaration
      }, () => {
        VisitContext.replaceVisit(VisitContext.visit);
        AppContext.registerSuccess('The visit was sent successfully.');
      });
    }
  };

  saveForNextDeclarant() {
    let componentNames = ['app-port-departure', '.validate-for-next-declarant', 'app-next-exit-point', 'app-next-next-ports'];
    if (!this.allComponentsAreValid(componentNames)) {
      return;
    }

    sendCommand('com.portbase.bezoekschip.common.api.visit.SaveNextVisitDeclaration', <SaveNextVisitDeclaration>{
      crn: VisitContext.visit.crn,
      nextVisitDeclaration: VisitContext.visit.nextVisitDeclaration,
    }, () => {
      VisitContext.replaceVisit(VisitContext.visit);
      AppContext.registerSuccess('The changes were stored successfully.');
    });
  }

  finishTerminalPlanning() {
    sendCommand('com.portbase.bezoekschip.common.api.visit.terminal.FinishTerminalPlanning', <FinishTerminalPlanning>{
      crn: VisitContext.visit.crn
    }, () => {
      VisitContext.replaceVisit(VisitContext.visit);
      AppContext.registerSuccess('Planning approved successfully.');
      this.router.navigate(['/details/' + VisitContext.visit.crn]);
    });
  }

  findLatestDeclaration = (): Declaration => this.context.findLatestDeclaration(
    this.context.visitInTerminalPlanning() ? DeclarationType.TERMINAL_PLANNING : DeclarationType.VISIT);

  latestVisitDeclarationsRejectReasons(): string[] {
    const type: DeclarationType[] = this.context.visitInTerminalPlanning()
      ? [DeclarationType.TERMINAL_PLANNING]
      : [DeclarationType.VISIT, DeclarationType.NOA, DeclarationType.NOD];
    return lodash.uniqBy(type.map(t => this.context.findLatestDeclaration(t))
      .filter(t => t)
      .map(t => t.rejectReasons)
      .filter(r => r), s => s);
  }

  private allComponentsAreValid(selectors: string[]): boolean {
    AppContext.clearVisitAlerts();
    const nativeElement: HTMLElement = this.element['nativeElement'] || this.element;

    function isValidElement(selector) {
      let nodeListOf = nativeElement.querySelectorAll<HTMLElement>(selector);
      let isValid = true;
      nodeListOf.forEach(element => {
        let isValidComponent = checkValidity(element, false);
        isValid = isValid && isValidComponent;
      });
      return isValid;
    }

    let someAreNotValid = selectors.map(selector => {
      return isValidElement(selector);
    }).some(cb => {
      return !cb
    });

    return !someAreNotValid;
  }

  private syncTimesForOrders = () => {
    if (VisitContext.visit.orderIncomingMovement && VisitContext.isExpected()) {
      if (VisitContext.visit.visitDeclaration.portVisit.firstMovement.order) {
        VisitContext.visit.visitDeclaration.portVisit.portEntry.requestedEtaPilotBoardingPlace =
          VisitContext.visit.visitDeclaration.portVisit.portEntry.etaPilotBoardingPlace;
        if (VisitContext.visit.additionalIncomingMovementInfo &&
          VisitContext.visit.additionalIncomingMovementInfo.etaPilotBoardingPlace !==
          VisitContext.visit.visitDeclaration.portVisit.portEntry.requestedEtaPilotBoardingPlace) {
          VisitContext.visit.additionalIncomingMovementInfo.etaPilotBoardingPlace = null;
        }
      } else {
        VisitContext.visit.visitDeclaration.portVisit.portEntry.requestedEtaPilotBoardingPlace = null;
      }
    }
    VisitContext.visit.visitDeclaration.portVisit.berthVisits.forEach(v => {
      if (!v.atd) {
        v.requestedEtd = v.nextMovement.order ? v.etd : null;
        const berthVisitInfo = VisitContext.visit.berthVisitInfos[v.id];
        if (berthVisitInfo && berthVisitInfo.harbourMasterInfo &&
          berthVisitInfo.harbourMasterInfo.etd !== v.requestedEtd) {
          VisitContext.visit.berthVisitInfos[v.id].harbourMasterInfo.etd = null;
        }
      }
    })
  };

  private setPositionsForBerthsWithBollards() {
    VisitContext.visit.visitDeclaration.portVisit.berthVisits.forEach(berthVisit => {
        if (!lodash.isEmpty(berthVisit.berth?.positions)) {
          berthVisit.startPosition = this.getPositionForBollard(berthVisit.bollardFrom, berthVisit);
          berthVisit.endPosition = this.getPositionForBollard(berthVisit.bollardTo, berthVisit);
        }
        berthVisit.position = berthVisit.berth.ownPosition || null
      }
    );
  }

  private getPositionForBollard(bollard: number, berthVisit: BerthVisit) {
    let position = berthVisit.berth.positions[bollard?.toString()];
    if (!position) {
      if (bollard) Sentry.captureMessage("Could not get position for " + bollard + " in berth " + berthVisit.berth.code, "warning")
      return null;
    }
    return position;
  }

  private validateVisit(): boolean {
    const result = checkValidity(this.element);
    let visit = VisitContext.visit;

    if (!result) {
      return false;
    }

    // A visit created without a port entry (e.g. from a VID) can be saved as a draft, but the port
    // entry must be defined before it can be declared: the backend requires a non-null portEntry for
    // an incoming-movement visit. Block the declare with a clear message instead of sending a null
    // portEntry (which fails server-side with a NullPointerException).
    if (visit.orderIncomingMovement && !visit.visitDeclaration.portVisit.portEntry) {
      AppContext.registerError('Please define the port entry in the Port arrival section before declaring the visit.');
      return false;
    }

    const now = moment();

    // incoming orders
    if (visit.orderIncomingMovement && VisitContext.isExpected()) {
      const incomingOrder: IncomingOrder = this.context.getIncomingOrder(visit);
      const savedIncomingOrder: IncomingOrder = this.context.getIncomingOrder(VisitContext.savedVisit);

      let newOrChangedOrder = incomingOrder.ordered && (!savedIncomingOrder || JSON.stringify(savedIncomingOrder) !== JSON.stringify(incomingOrder));
      if (newOrChangedOrder) {
        const hasTugBoatsOrPilotService = (incomingOrder.pilotService && incomingOrder.pilotService.required) ||
          (incomingOrder.passingThrough ? (incomingOrder.passingThroughTugboats && incomingOrder.passingThroughTugboats.required) :
            (incomingOrder.tugboatAtArrival && incomingOrder.tugboatAtArrival.required));
        const hasBoatMenOrdered = incomingOrder.boatmenAtArrival && incomingOrder.boatmenAtArrival.required;
        const hasHelmsmenOrdered = incomingOrder.helmsmenAtArrival && incomingOrder.helmsmenAtArrival.required;

        // ALLEEN VOOR ZEE
        const incomingMovementHarbourMasterInfo = this.context.visit.incomingMovementHarbourMasterInfo;
        if (!incomingMovementHarbourMasterInfo || !incomingMovementHarbourMasterInfo.pilotOnBoard) {
          if (incomingOrder.pilotStation.atSea) {
            const incomingAtSea = incomingOrder.deadlines.incomingAtSea;
            if (incomingOrder.pilotStation.code === 'MC' || incomingOrder.pilotStation.code === 'KP' || incomingOrder.pilotStation.code === 'E13' || !incomingOrder.pilotStation.earlyDeadlineForVisit) {
              if (hasTugBoatsOrPilotService) {
                const readableString = DurationComponent.durationToReadableString(incomingAtSea.pilotOrTugboatDeadline);
                this.validateDeadlineAndRaiseError(incomingOrder.etaPilotBoardingPlace, incomingAtSea.pilotOrTugboatDeadline,
                  `Incoming movement should be ordered at least ${readableString} before arrival at pilot boarding place (${incomingOrder.pilotStation.name}) when pilots or tugboats are required`);
              } else {
                if (hasBoatMenOrdered) {
                  const readableString = DurationComponent.durationToReadableString(incomingAtSea.boatmenDeadline);
                  this.validateDeadlineAndRaiseError(incomingOrder.etaPilotBoardingPlace, incomingAtSea.boatmenDeadline,
                    `Incoming movement should be ordered at least ${readableString} before arrival at pilot boarding place (${incomingOrder.pilotStation.name}) when only boatmen are required`);
                } else if (hasHelmsmenOrdered) {
                  const readableString = DurationComponent.durationToReadableString(incomingAtSea.helmsmenDeadline);
                  this.validateDeadlineAndRaiseError(incomingOrder.etaPilotBoardingPlace, incomingAtSea.helmsmenDeadline,
                    `Incoming movement should be ordered at least ${readableString} before arrival at pilot boarding place (${incomingOrder.pilotStation.name}) when only helmsmen are required`);
                } else {
                  const readableString = DurationComponent.durationToReadableString(incomingAtSea.defaultDeadline);
                  this.validateDeadlineAndRaiseError(incomingOrder.etaPilotBoardingPlace, incomingAtSea.defaultDeadline,
                    `Incoming movement should be ordered at least ${readableString} before arrival at pilot boarding place (${incomingOrder.pilotStation.name}) when no nautical services are required`);
                }
              }
            } else if (incomingAtSea.earlyDeadlineTime) {
              const readableString = DurationComponent.durationToReadableString(incomingAtSea.earlyDeadlineTime);
              this.validateDeadlineAndRaiseError(incomingOrder.etaPilotBoardingPlace, incomingAtSea.earlyDeadlineTime,
                `Incoming movement should be ordered at least ${readableString} before arrival at pilot boarding place (${incomingOrder.pilotStation.name})`);
            }
          } else { // Achterland only
            const incomingAtHinterland = incomingOrder.deadlines.incomingAtHinterland;
            if (hasTugBoatsOrPilotService) {
              const readableString = DurationComponent.durationToReadableString(incomingAtHinterland.pilotOrTugboatDeadline);
              this.validateDeadlineAndRaiseError(incomingOrder.etaPilotBoardingPlace, incomingAtHinterland.pilotOrTugboatDeadline,
                `Pilots and tugboats should be ordered at least ${readableString} before arrival`);
            } else {
              if (!incomingOrder.passingThrough && hasBoatMenOrdered) {
                const readableString = DurationComponent.durationToReadableString(incomingAtHinterland.boatmenDeadline);
                this.validateDeadlineAndRaiseError(incomingOrder.etaPilotBoardingPlace, incomingAtHinterland.boatmenDeadline,
                  `Boatmen should be ordered at least ${readableString} before arrival`);
              }
              if (!incomingOrder.passingThrough && hasHelmsmenOrdered) {
                const readableString = DurationComponent.durationToReadableString(incomingAtHinterland.helmsmenDeadline);
                this.validateDeadlineAndRaiseError(incomingOrder.etaPilotBoardingPlace, incomingAtHinterland.helmsmenDeadline,
                  `Helmsmen should be ordered at least ${readableString} before arrival`);
              }
              if ((!incomingOrder.passingThrough && !hasBoatMenOrdered) || incomingOrder.passingThrough) {
                const readableString = DurationComponent.durationToReadableString(incomingAtHinterland.defaultDeadline);
                this.validateDeadlineAndRaiseError(incomingOrder.etaPilotBoardingPlace, incomingAtHinterland.defaultDeadline,
                  `Movement should be ordered at least ${readableString} before arrival`);
              }
            }
          }
        }
      }
    }

    // Storm Pilotage
    let portvisit = visit.visitDeclaration.portVisit;
    let lastMovement = VisitContext.getLastMovement();
    if(portvisit.firstMovement) {
      this.createStormPilotageError(visit, portvisit, portvisit.firstMovement, true);
    }
    if(lastMovement) {
      this.createStormPilotageError(visit, portvisit, lastMovement, false);
    }

    if (visit.orderNauticalServices) {
      const orders: Order[] = getOrders(visit);
      const savedOrders: Order[] = getOrders(VisitContext.savedVisit);
      orders.filter(o => {
        const savedOrder = savedOrders.find(so => so.id === o.id);
        return !savedOrder || JSON.stringify(savedOrder) !== JSON.stringify(o);
      }).forEach(o => {
        function hasPilotOnBoard(berthVisitInfos: { [p: string]: BerthVisitInfo }, id: string) {
          return berthVisitInfos[id] && berthVisitInfos[id].harbourMasterInfo
            && berthVisitInfos[id].harbourMasterInfo.pilotOnBoard;
        }

        if (!o.atd && !hasPilotOnBoard(this.context.visit.berthVisitInfos, o.id)) {
          if (o.pilotService.required || (o.tugboatAtDeparture && o.tugboatAtDeparture.required)) {
            const readableString = DurationComponent.durationToReadableString(o.deadlines.pilotOrTugboatDeadline);
            this.validateDeadlineAndRaiseError(o.etd, o.deadlines.pilotOrTugboatDeadline,
              `Pilots and tugboats should be ordered at least ${readableString} before departure`);
          }
          if (o.boatmenAtDeparture?.required) {
            const readableString = DurationComponent.durationToReadableString(o.deadlines.boatmenDeadline);
            this.validateDeadlineAndRaiseError(o.etd, o.deadlines.boatmenDeadline,
              `Boatmen should be ordered at least ${readableString} before departure`);
          }
          if (o.helmsmenAtDeparture?.required) {
            const readableString = DurationComponent.durationToReadableString(o.deadlines.helmsmenDeadline);
            this.validateDeadlineAndRaiseError(o.etd, o.deadlines.helmsmenDeadline,
              `Helmsmen should be ordered at least ${readableString} before departure`);
          }
          if (moment(o.etd).subtract(DurationComponent.toDuration(o.deadlines.defaultDeadline)).isBefore(now)) {
            const readableString = DurationComponent.durationToReadableString(o.deadlines.defaultDeadline);
            AppContext.registerError(`An order should be placed at least ${readableString} before departure`);
          }
        }
      });
    }

    // Exemptions
    if (this.appContext.showExemptions()) {
      for (const exemption of Object.values(visit.visitDeclaration.exemptions)) {
        if (!exemption.hasFormFilledTruthfully) {
          AppContext.registerError("The exemption(s) should be declared truthfully");
          break;
        }
        if ((!exemption.attachments || exemption.attachments.length === 0) &&
          attachmentRequired(exemption)) {
          AppContext.registerError("Attachment(s) required for the exemption(s)");
          break;
        }
        if (vesselsRequired(exemption.exemptionType) &&
            (exemption as FormVessels).vessels.length === 0) {
          AppContext.registerError("Vessel(s) required for the exemption(s)");
          break;
        }
        if (inertTanksRequired(exemption.exemptionType) &&
            !(exemption as FormPetroleumPort).withinTheZoningScheme) {
          AppContext.registerError("Please use the form in MyPort for request for exemption to berth outside a Petroleumhaven");
          break;
        }
      }
    }

    // contact details
    if(visit.portOfCall.port.locationUnCode == "NLRTM") {
      this.validateVisitContactDetails(visit)
    }

    return !AppContext.hasErrors();
  }

  private validateDeadlineAndRaiseError(timeToCheck: string, deadline: string, errorMessage: string): void {
    const now = moment();
    if (moment(timeToCheck).subtract(DurationComponent.toDuration(deadline)).isBefore(now)) {
      AppContext.registerError(errorMessage);
    }
  }

  createStormPilotageError(visit: Visit, portvisit: PortVisit, movement: LocalMovement, isFirstMovement: boolean) {
    let isLoaRequired = VisitContext.isLoaRequired(movement, visit.vessel.fullLength, isFirstMovement);
    let noPECHolder = !this.hasPECHolder(movement);
    let stormPilotageInformationMovement = movement.stormPilotageInformation;
    let remotePilotageNotAccepted = !stormPilotageInformationMovement
      || stormPilotageInformationMovement.remotePilotage !== true;
    let pilotServiceRequired = movement.pilotService?.required;
    let loaQuestionsShown = VisitContext.loaQuestionsShown(visit.vessel.fullLength, movement, isFirstMovement);

    if (isFirstMovement) {
      let isInboundForRotterdam = VisitContext.isRotterdamVisit()
        && visit.orderIncomingMovement
        && portvisit.portEntry.origin === 'SEA'
        && portvisit.portEntry.intention === 'REQUEST_FOR_ENTRY'
        && (!portvisit.ataPort && !portvisit.atdPort && !portvisit.berthVisits.find(v => !!v.ata));
      let notHaveFirstBerthAta = !portvisit.berthVisits[0]?.ata;

      if (loaQuestionsShown
        && notHaveFirstBerthAta
        && isLoaRequired
        && noPECHolder
        && remotePilotageNotAccepted
        && isInboundForRotterdam
        && pilotServiceRequired) {
        AppContext.registerError("Please note that during the storm pilotage, it is required to make use of the shore based pilotage. " +
          "If the captain doesn’t accept the shore based pilotage, it is not allowed to enter the Port of Rotterdam.")
      }
    } else {
      if (loaQuestionsShown
        && isLoaRequired
        && noPECHolder
        && remotePilotageNotAccepted
        && VisitContext.isRotterdamVisit()
        && pilotServiceRequired) {
        AppContext.registerError("Please note that during the storm pilotage, it is required to make use of the shore based pilotage. " +
          "If the captain doesn’t accept the shore based pilotage, it is not allowed to exit the Port of Rotterdam.")
      }
    }
  }

  doCancelVisit = () => {
    sendCommand('com.portbase.bezoekschip.common.api.visit.CancelVisit', <CancelVisit>{
      crn: VisitContext.visit.crn
    }, () => {
      VisitContext.visit.cancelled = true;
      VisitContext.replaceVisit(VisitContext.visit);
      AppContext.registerSuccess('The visit was cancelled successfully.');
    });
  };

  cancelVisit = () => {
    openConfirmationModalWithCallback((confirmed) => {
      if (confirmed) {
        this.doCancelVisit();
      }
    }, ModalConfirmAutofocus, <ModalConfirmAutofocusData>{
      type: "danger",
      title: "Danger",
      message: "Once a visit is cancelled it is no longer possible to make changes.",
      question: "Are you sure that you want to cancel this visit?",
      confirmText: "Yes",
      cancelText: "No"
    }, 'static');
  };

  getBerthCode(id: string): string {
    const berthVisit = VisitContext.visit.visitDeclaration.portVisit.berthVisits.find(bv => {
      return bv.id === id
    });
    if (berthVisit && berthVisit.berth) {
      return berthVisit.berth.code;
    }
    return null;
  }

  hasAtd(id: string): boolean {
    const berthVisit = VisitContext.visit.visitDeclaration.portVisit.berthVisits.find(bv => {
      return bv.id === id
    });
    return !!berthVisit?.atd;
  }

  departureCarrierRequired(): boolean {
    const berthVisits = VisitContext.visit.visitDeclaration.portVisit.berthVisits;
    return berthVisits.length > 0 ? !!berthVisits[berthVisits.length - 1].etd : false;
  }

  getShiftedOrders(): BerthVisit[] {
    return VisitContext.savedVisit.berthVisitInfos ? VisitContext.savedVisit.visitDeclaration.portVisit.berthVisits.filter(v => {
      const berthVisitInfo = VisitContext.savedVisit.berthVisitInfos[v.id];
      return berthVisitInfo && berthVisitInfo.harbourMasterInfo && berthVisitInfo.harbourMasterInfo.etd &&
        v.requestedEtd && !moment(berthVisitInfo.harbourMasterInfo.etd).isSame(v.requestedEtd);
    }) : [];
  }

  private setEtaPort() {
    if (VisitContext.isExpected()) {
      const portEntry = VisitContext.visit.visitDeclaration.portVisit.portEntry;
      // A visit created without a port entry has no portEntry until one is chosen; nothing to derive.
      if (!portEntry) {
        return;
      }

      if (portEntry.origin === "SEA") {
        VisitContext.visit.visitDeclaration.portVisit.etaPort = (<SeaEntry>portEntry).etaSeaBuoy;
      } else {
        VisitContext.visit.visitDeclaration.portVisit.etaPort = (<HinterlandEntry>portEntry).etaPilotBoardingPlace;
      }
    }
  }

  get userIsNextDeclarantOrNextOwner(): boolean {
    return VisitContext.isOrganisationNextDeclarant();
  }

  userIsDeclarantOrOwner() {
    return VisitContext.isOrganisationCurrentVisitDeclarant()
  }

  userIsAllowedToSave() {
    const declaration: Declaration = VisitContext.visit.portOfCall.paDeclarationRequired ?
      VisitContext.findLatestDeclaration(DeclarationType.VISIT) : null;
    const hasNoDeclaration = (!declaration || Object.keys(declaration).length === 0);

    return hasNoDeclaration && !this.userIsNextDeclarantOrNextOwner;
  }

  userIsAllowedToDeclareVisit() {
    let paDeclarationRequired = VisitContext.visit.portOfCall.paDeclarationRequired;
    return paDeclarationRequired && !this.userIsNextDeclarantOrNextOwner && this.userIsDeclarantOrOwner();
  }

  userIsNextOwnerOrDeclarant() {
    return VisitContext.isOrganisationNextDeclarant();
  }

  isDisabled() {
    const declaration = VisitContext.findLatestDeclaration(DeclarationType.VISIT);
    return (!this.emergencyProcedureService.isEmergencyProcedureEnabledForPort(VisitContext.visit.portOfCall.port.locationUnCode) && declaration && declaration.status === 'DECLARED') || VisitContext.isUserOnlyCargoDeclarant();
  }

  private userIsDeclarant(): boolean {
    return this.appContext.userProfile.organisation?.shortName === this.context.visit.declarant.shortName;
  }

  userIsOwner(): boolean {
    return this.context.visit.owner ?
      this.appContext.userProfile.organisation?.shortName === this.context.visit.owner.shortName : false;
  }

  isPermittedToAddBerth() {
    return this.userIsDeclarant() || this.userIsNextOwnerOrDeclarant() || this.appContext.isAdmin() || this.userIsOwner();
  }

  addBerth = () => {
    if (this.userIsDeclarant() || this.userIsOwner() || this.appContext.isAdmin()) {
      let berthVisits = this.context.visit.visitDeclaration.portVisit.berthVisits;
      let newBerthVisit = <BerthVisit>{
        id: uuid(),
        nextMovement: {},
        visitPurposes: []
      };
      if (this.context.visit.nextDeclarant) {
        berthVisits.splice(berthVisits.length - 1, 0, newBerthVisit);
      } else {
        berthVisits.push(newBerthVisit);
      }
      this.context.visit.visitDeclaration.portVisit.passingThroughTugboats = null;

    } else if (this.userIsNextOwnerOrDeclarant()) {
      this.context.visit.nextVisitDeclaration.nextBerthVisits = this.context.visit.nextVisitDeclaration.nextBerthVisits || [];
      this.context.visit.nextVisitDeclaration.nextBerthVisits.push(<BerthVisit>{
        id: uuid(),
        nextMovement: {},
        visitPurposes: []
      });
    } else {
      this.appContext.addAlert(<Alert>{
        content: "You are not allow to add a new berth visit", level: "warning", type: "visit"
      })
    }
  };

  hasPECHolder(movement: LocalMovement): boolean {
    return movement.pilotExemption && !!movement.pilotExemption.holderName;
  }

  private showSendWarningsModal(cancellingOrder: boolean) {
    function setCancellationReasonForCancelledOrders(context, reason: string) {
      const incomingOrder: IncomingOrder = VisitContext.getIncomingOrder(context.visit);
      const savedIncomingOrder: IncomingOrder = VisitContext.getIncomingOrder(context.savedVisit);
      if (!incomingOrder.ordered && savedIncomingOrder.ordered) {
        context.visit.visitDeclaration.portVisit.firstMovement.orderCancellationReason = <OrderCancellationReason>reason;
      }
      const orders: Order[] = getOrders(context.visit);
      const savedOrders: Order[] = getOrders(context.savedVisit);
      savedOrders.filter(so => !orders.find(o => so.id === o.id)).forEach((v: Order) => {
        const nextMovement = VisitContext.berthVisitInVisitById(context.visit, v.id)?.nextMovement;

        if (!!nextMovement) {
          nextMovement.orderCancellationReason = <OrderCancellationReason>reason;
        }
      });
    }

    openConfirmationModalWithCallback((confirmed, reason) => {
      if (confirmed) {
        if (reason) {
          setCancellationReasonForCancelledOrders(this.context, reason);
        }
        this.doSendVisit();
      }
      this.sendWarnings = [];
    }, ModalConfirmAutofocus, <ModalConfirmAutofocusData>{
      type: "warning",
      title: "Warning",
      message: "Are you sure that you want to send the declaration?",
      beforePointsMessage: "Note the following:",
      points: this.sendWarnings,
      withChosableReason: cancellingOrder,
      chosableMessage: "Please provide a justification for cancelling orders:",
      chosableReasons: VisitComponent.cancelReasons,
      chosableReasonFormatter: VisitComponent.cancelReasonFormatter,
      confirmText: "Send"
    }, 'static');
  }

  static cancelReasons: OrderCancellationReason[] = [OrderCancellationReason.TERMINAL_NOT_READY, OrderCancellationReason.VESSEL_NOT_READY, OrderCancellationReason.BUNKERING_NOT_READY,
    OrderCancellationReason.PAPERWORK_NOT_READY, OrderCancellationReason.OTHER_CUSTOMER_ORDERS, OrderCancellationReason.SURVEYOR_NOT_READY];
  static cancelReasonFormatter = (value: OrderCancellationReason) => {
    switch (value) {
      case 'TERMINAL_NOT_READY':
        return 'The terminal is not ready';
      case 'VESSEL_NOT_READY':
        return 'The vessel is not ready';
      case 'BUNKERING_NOT_READY':
        return 'The bunkering is not ready';
      case 'PAPERWORK_NOT_READY':
        return 'The paperwork is not ready';
      case 'OTHER_CUSTOMER_ORDERS':
        return 'Other customer orders';
      case 'SURVEYOR_NOT_READY':
        return 'The surveyor is not ready';
      default:
        throw Error('Unknown order cancellation reason of value: ' + value);
    }
  };

  visitOlderThanOneYear() {
    const atdPort = VisitContext.savedVisit.visitDeclaration.portVisit.atdPort;

    if (!atdPort) {
      return false;
    }

    const atdPortDate = new Date(atdPort);
    if (isNaN(atdPortDate.getTime())) {
      return false;
    }

    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

    return atdPortDate < oneYearAgo;
  }

  showOrderOverdueWarning(): boolean {
    const isEligibleForOrderOverdueWarning = VisitContext?.savedVisit?.eligibleForOrderOverdueWarning;
    const etaPilotBoardingPlace = VisitContext
        ?.savedVisit
        ?.visitDeclaration
        ?.portVisit
        ?.portEntry
        ?.etaPilotBoardingPlace;
    const etaPilotBoardingPlaceInPast = etaPilotBoardingPlace
        && moment(moment.now()).isAfter(moment(etaPilotBoardingPlace));
    return isEligibleForOrderOverdueWarning && etaPilotBoardingPlaceInPast;
  }

  showEmergencyProcedureWarning(): boolean {
    return this.emergencyProcedureService.isEmergencyProcedureEnabledForPort(VisitContext.visit.portOfCall.port.locationUnCode);
  }

  getPortName(): string {
    return VisitContext.visit.portOfCall.port.name;
  }

  private processMultiBerths(visitDeclaration: VisitDeclaration): Observable<string[]> {
    const tasks: Observable<string>[] = [];
    VisitContext.multiBerthLoadDischargeInfo?.forEach((value, key) => {
      const berthVisit: BerthVisit = visitDeclaration.portVisit.berthVisits.find(bv => bv.id === key);
      if (VisitContext.isMultiBerthTerminal(berthVisit)) {
        let indexOfBerthVisit = visitDeclaration.portVisit.berthVisits.indexOf(berthVisit);
        value.filter(this.berthVisitWithLoadDischargeInfo).forEach((info, index) => {
          const berthVisitToUse: BerthVisit = index === 0 ? berthVisit : cloneDeep(berthVisit);
          berthVisitToUse.quay = info.quay;
          berthVisitToUse.load = info.load;
          berthVisitToUse.discharge = info.discharge;
          berthVisitToUse.restow = info.restow;
          if (index > 0) {
            berthVisitToUse.id = uuid();
            const nextCallId: Observable<string> = VisitContext.getNextCallId();
            tasks.push(nextCallId);
            nextCallId.subscribe(value => berthVisitToUse.callId = value.toString());
            visitDeclaration.portVisit.berthVisits.splice(indexOfBerthVisit + 1, 0, berthVisitToUse);
          }
        });
      }
    });
    return tasks.length > 0 ? forkJoin(tasks) : of([]);
  }

  private berthVisitWithLoadDischargeInfo = (v: LoadDischargeInfo) => v.discharge > 0 || v.load > 0 || v.restow > 0;

  private validateVisitContactDetails(visit: Visit) {
    if(!!visit.visitDeclaration.vesselEmailAddress && !this.isValidEmail(visit.visitDeclaration.vesselEmailAddress)) {
      AppContext.registerError("The value given for Vessel email address is not a valid email address")
    }
    if(!!visit.visitDeclaration.vesselPhoneNumber && !this.isValidPhone(visit.visitDeclaration.vesselPhoneNumber)) {
      AppContext.registerError("The value given for Vessel phone number is not a valid phone number")
    }
  }

  private isValidEmail(email: string | null | undefined): boolean {
    const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (email == null || email.trim() === "") {
      return false;
    }
    return EMAIL_REGEX.test(email.trim());
  }

  private isValidPhone(phone: string | null | undefined): boolean {
    const PHONE_REGEX_E164 = /^\+?[0-9][0-9 -]{6,19}$/;
    if (phone == null || phone.trim() === "") {
      return false;
    }
    return PHONE_REGEX_E164.test(phone.trim());
  }
}

interface Order {
  id: string;
  etd: string;
  atd: string;
  tugboatAtDeparture: TugboatService;
  boatmenAtDeparture: BoatmenService;
  helmsmenAtDeparture: HelmsmenService;
  berth: Berth;
  pilotService: PilotService;
  deadlines: OrderDeadlines;
}

function getOrders(visit: Visit): Order[] {
  const result: Order[] = [];
  for (const v of visit.visitDeclaration.portVisit.berthVisits) {
    if (v.nextMovement.order) {
      result.push({
        id: v && v.id,
        etd: v && v.etd,
        atd: v && v.atd,
        tugboatAtDeparture: v && v.tugboatAtDeparture,
        boatmenAtDeparture: v && v.boatmenAtDeparture,
        helmsmenAtDeparture: v && v.helmsmenAtDeparture,
        pilotService: v.nextMovement.pilotService,
        berth: v.berth,
        deadlines: visit.portOfCall.orderDeadlines.betweenBerths
      })
    }
  }
  return result;
}


